/**
 * Query key factory for question-related queries
 * Provides consistent query key generation for React Query cache management
 *
 * @example
 * ```typescript
 * // Get all questions
 * queryClient.invalidateQueries({ queryKey: questionKeys.all });
 *
 * // Get specific question
 * queryClient.invalidateQueries({ queryKey: questionKeys.detail('Q123') });
 *
 * // Get specific translation
 * queryClient.invalidateQueries({ queryKey: questionKeys.translation('Q123', 'es') });
 * ```
 */
export const questionKeys = {
  /**
   * Base key for all question-related queries
   */
  all: ['questions'] as const,

  /**
   * Key for a specific question detail
   * @param questionId - The unique identifier of the question
   */
  detail: (questionId: string) => [...questionKeys.all, 'detail', questionId] as const,

  /**
   * Key for a specific question translation
   * @param questionId - The unique identifier of the question
   * @param language - The language code (e.g., 'es', 'fr', 'de')
   */
  translation: (questionId: string, language: string) =>
    [...questionKeys.all, 'translation', questionId, language] as const,

  /**
   * Key for all translations of a specific question
   * @param questionId - The unique identifier of the question
   */
  translations: (questionId: string) =>
    [...questionKeys.all, 'translations', questionId] as const,

  /**
   * Key for question list queries
   */
  list: () => [...questionKeys.all, 'list'] as const,

  /**
   * Key for question search queries
   * @param searchParams - The search parameters used
   */
  search: (searchParams: Record<string, any>) =>
    [...questionKeys.all, 'search', searchParams] as const,

  /**
   * Mutation key for updating a specific question translation
   * @param questionId - The unique identifier of the question
   * @param language - The language code (e.g., 'es', 'fr', 'de')
   */
  updateTranslation: (questionId: string, language: string) =>
    [...questionKeys.all, 'updateTranslation', questionId, language] as const,
};
